import React, { useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import { Face } from "@mui/icons-material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { useRecognitionPieSocket } from "../hooks/useRecognitionPieSocket";
import SingleDatePicker from "../utils/singleDate";
import { CalendarWithActions } from "./CalendarWithActions";

// Known, Unknown (grey), Possible Match, Failed (red)
const COLORS_BY_LABEL = {
  "Known Match": "#10b981",
  "Unknown Match": "#6b7280",
  "Possible Match": "#f59e0b",
  "Failed": "#ef4444",
};
const COLORS = ["#10b981", "#6b7280", "#f59e0b", "#ef4444"];
const FALLBACK_COLOR = "#d4d4d4";

const LABEL_TO_FILTER = {
  "Known Match": "match",
  "Unknown Match": "unmatch",
  "Possible Match": "possible_match",
  "Failed": "failed",
};

// Build pie data from API result (chart_data or legacy known/unknown)
const getChartData = (result) => {
  const chartData = result.chart_data;
  if (chartData && Array.isArray(chartData) && chartData.length > 0) {
    const total = chartData.reduce((s, d) => s + (d.value || 0), 0);
    if (total === 0) {
      return [
        { name: "No Data", value: 1, displayValue: 0, isFallback: true },
      ];
    }
    return chartData
      .filter((d) => (d.value || 0) > 0)
      .map((d) => ({
        name: d.label,
        value: d.value,
        displayValue: d.value,
        color: COLORS_BY_LABEL[d.label] ?? d.color,
        isFallback: false,
      }));
  }
  const known = result.known_users || 0;
  const unknown = result.unknown_users || 0;
  const possible = result.possible_match_count || 0;
  const failed = result.failed_count || 0;
  const total = known + unknown + possible + failed;
  if (total === 0) {
    return [
      { name: "No Data", value: 1, displayValue: 0, isFallback: true },
    ];
  }
  return [
    { name: "Known Match", value: known, displayValue: known, color: COLORS_BY_LABEL["Known Match"], isFallback: false },
    { name: "Unknown Match", value: unknown, displayValue: unknown, color: COLORS_BY_LABEL["Unknown Match"], isFallback: false },
    { name: "Possible Match", value: possible, displayValue: possible, color: COLORS_BY_LABEL["Possible Match"], isFallback: false },
    { name: "Failed", value: failed, displayValue: failed, color: COLORS_BY_LABEL["Failed"], isFallback: false },
  ].filter((d) => d.value > 0);
};

const PieChartCard = () => {
  const navigate = useNavigate();
  const [pickerDate, setPickerDate] = useState(new Date());
  const [appliedDate, setAppliedDate] = useState(new Date());
  const datePickerRef = useRef(null);
  const calendarActionsRef = useRef({});
  const calendarContainer = useRef((props) => (
    <CalendarWithActions
      {...props}
      onApply={() => calendarActionsRef.current.onApply?.()}
      onCancel={() => calendarActionsRef.current.onCancel?.()}
      applyDisabled={calendarActionsRef.current.applyDisabled}
    />
  )).current;

  const appliedDateStr = format(appliedDate, "yyyy-MM-dd");
  const pickerDateStr = format(pickerDate, "yyyy-MM-dd");
  const isApplyPending = pickerDateStr !== appliedDateStr;

  const {
    data,
    error,
    loading,
    refresh,
    totalRecognitions,
  } = useRecognitionPieSocket({
    singleDate: appliedDateStr,
    getChartData,
  });

  const handlePickerDateChange = (pickedDate) => {
    setPickerDate(pickedDate);
  };

  const handleApplyClick = () => {
    const dateToApply = format(pickerDate, "yyyy-MM-dd");
    setAppliedDate(pickerDate);
    refresh(dateToApply, { showLoading: true });
    datePickerRef.current?.setOpen(false);
  };

  const handleCancelClick = () => {
    setPickerDate(appliedDate);
    datePickerRef.current?.setOpen(false);
  };

  calendarActionsRef.current = {
    onApply: handleApplyClick,
    onCancel: handleCancelClick,
    applyDisabled: loading,
  };

  const handleLegendClick = (entryName) => {
    const filter = LABEL_TO_FILTER[entryName] != null ? LABEL_TO_FILTER[entryName] : "match";
    navigate(`/in-out?logs_status=${filter}&start_date=${appliedDateStr}&end_date=${appliedDateStr}`);
  };

  if (loading) {
    return (
      <Card sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CardContent>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography color="error">Error: {error}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        maxWidth: "100%",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        transition: "all 0.3s ease-in-out",
        border: "1px solid rgba(64, 81, 138, 0.12)",
        borderRadius: 3,
        boxShadow: "0 10px 28px rgba(31, 45, 84, 0.12)",
        "&:hover": { boxShadow: "0 12px 32px rgba(0,0,0,0.18)" },
        overflow: "visible",
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: { xs: 1.75, sm: 2.25, md: 2.5 }, overflow: "visible" }}>
        
        {/* ---------- HEADER ---------- */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.25, flexWrap: "wrap", gap: { xs: 1.5, sm: 2 }, alignItems: { xs: "stretch", sm: "center" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.25, sm: 1.5 }, minWidth: 0 }}>
            <Box sx={{ p: { xs: 1.2, sm: 1.5 }, background: "rgba(102, 126, 234, 0.1)", borderRadius: 2, display: "flex", alignItems: "center" }}>
              <Face sx={{ color: "#667eea", fontSize: { xs: 22, sm: 24 } }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#2d3748", lineHeight: 1.25, fontSize: { xs: "1.12rem", sm: "1.25rem" } }}
            >
              Face Recognition Statistics
            </Typography>
          </Box>

          {/* ---------- DATE PICKER ---------- */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
            <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
              <SingleDatePicker
                ref={datePickerRef}
                selectedDate={pickerDate}
                onChange={handlePickerDateChange}
                disableFuture
                shouldCloseOnSelect={false}
                calendarContainer={calendarContainer}
                popperPlacement="bottom-end"
              />
            </Box>
          </Box>
        </Box>
        {isApplyPending && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
    
          </Typography>
        )}

        {/* ---------- TOTAL COUNT ---------- */}
        <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Total Recognitions: <strong>{totalRecognitions}</strong>
          </Typography>
        </Box>

        {/* ---------- CHART SECTION: PIE + LEGEND (legend right, single column, names aligned from start) ---------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 2, md: 3 },
            width: "100%",
            flexWrap: "wrap",
            flex: 1,
            minHeight: { xs: 280, md: 300 },
            py: { xs: 1, md: 2 },
          }}
        >
          {/* ---------- PIE CHART: use full area with larger radius ---------- */}
          <Box sx={{ flex: "1 1 0", width: "100%", minWidth: 200, maxWidth: { xs: "100%", md: 320 }, height: { xs: 240, sm: 280 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  key={`pie-${appliedDateStr}-${data.length}-${totalRecognitions}`}
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius="99%"
                  dataKey="value"
                  isAnimationActive={false}
                  // Custom label to make percentage clickable like the corresponding slice
                  label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                    if (percent === 0) return null;
                    const entry = data[index];
                    if (!entry) return null;

                    const RADIAN = Math.PI / 180;
                    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);

                    const clickable = !entry.isFallback;
                    return (
                      <foreignObject
                        x={x - 22} // adapt as needed for centering on number
                        y={y - 14}
                        width={44}
                        height={28}
                        style={{ pointerEvents: "none" }} // disables events bubbling to svg pie slice
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontWeight: 600,
                            color: "#fff",
                            fontSize: 11,
                            cursor: clickable ? "pointer" : "default",
                            pointerEvents: "auto", // Re-enable pointer here!
                            userSelect: "none"
                          }}
                          tabIndex={clickable ? 0 : undefined}
                          role={clickable ? "button" : undefined}
                          aria-label={clickable ? `Show details for ${entry.name}` : undefined}
                          onClick={
                            clickable
                              ? (e) => {
                                  e.stopPropagation();
                                  handleLegendClick(entry.name);
                                }
                              : undefined
                          }
                          onKeyDown={
                            clickable
                              ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleLegendClick(entry.name);
                                  }
                                }
                              : undefined
                          }
                        >
                          {`${Math.round(percent * 100)}%`}
                        </div>
                      </foreignObject>
                    );
                  }}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`${entry.name}-${index}`}
                      fill={entry.isFallback ? FALLBACK_COLOR : (entry.color || COLORS[index % COLORS.length])}
                      onClick={() => entry && !entry.isFallback && handleLegendClick(entry.name)}
                      style={{ cursor: entry?.isFallback ? 'default' : 'pointer' }}
                    />
                  ))}
                </Pie>

                <Tooltip
                  formatter={(value, name, props) =>
                    `${props.payload.displayValue} recognitions`
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* ---------- LEGEND: single column, names aligned from start (left-aligned) ---------- */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1.25,
              flex: "0 0 auto",
              minWidth: { xs: "100%", md: 160 },
              width: { xs: "100%", md: "auto" },
            }}
          >
            {data.map((entry, idx) => (
              <Button
                key={idx}
                disabled={entry.isFallback}
                onClick={() => handleLegendClick(entry.name)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  textTransform: "none",
                  justifyContent: "flex-start",
                  width: "100%",
                  minWidth: 0,
                  p: 0.75,
                  borderRadius: 1.5,
                  border: "1px solid rgba(64, 81, 138, 0.08)",
                  bgcolor: "rgba(64, 81, 138, 0.04)",
                  "&:disabled": { opacity: 0.6 },
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                    bgcolor: entry.isFallback
                      ? FALLBACK_COLOR
                      : (entry.color || COLORS[idx % COLORS.length]),
                  }}
                />
                <Typography fontWeight={600} sx={{ textAlign: "left", fontSize: { xs: "0.83rem", sm: "0.9rem" } }}>
                  {entry.name}: {entry.displayValue}
                </Typography>
              </Button>
            ))}
          </Box>
        </Box>

      </CardContent>
    </Card>
  );
};

export default PieChartCard;
